import { Add } from 'iconsax-react';
import mergeRefs from 'merge-refs';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBatchForward, { type ForwardTarget } from '@/hooks/forward/use-batch-forward';
import useUserInfo from '@/hooks/users/use-user-info';
import useUserQrCode from '@/hooks/users/use-user-qrcode';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import Modal, { type ModalAction } from '../common/modal/modal';
import QrCodeView from '../common/qr-code-view';
import ForwardForm from '../scan-code/forward-form';
import LoadingLayout from './progress-layout/loading-layout';

type QrCodeModalProps = {
  clickOutsideToClose?: boolean;
  className?: string;
  contentClassName?: string;
  onClose?: () => void;
  defaultOpen?: boolean;
};

const QrCodeModal = forwardRef<ModalAction, QrCodeModalProps>(
  ({ clickOutsideToClose, onClose, defaultOpen = false }, ref) => {
    const dialogRef = useRef<ModalAction>(null);
    const forwardFormRef = useRef<ModalAction>(null);

    const [code, setCode] = useState('');

    const { t } = useTranslation();
    const { data: userInfo, isLoading: isUserInfoLoading } = useUserInfo();
    const { data: qrcode, isLoading: isUserQrCodeLoading } = useUserQrCode();
    const isLoading = isUserInfoLoading || isUserQrCodeLoading;
    const { mutateAsync } = useBatchForward();
    const handleForward = async (targets: ForwardTarget[], clearSelection: () => void) => {
      if (!code) return;
      if (await mutateAsync({ urls: [{ url: code, type: ConversationMessageContentType.Image }], targets })) {
        forwardFormRef.current?.close();
      } else {
        clearSelection();
      }
    };
    return (
      <>
        <Modal
          ref={mergeRefs(dialogRef, ref)}
          clickOutsideToClose={clickOutsideToClose}
          contentClassName='w-[400px] group modal-layout pb-6 pt-4'
          onClose={() => {
            setCode('');
          }}
          defaultOpen={defaultOpen}>
          <div className='flex justify-between mb-6'>
            <div className='font-bold text-h1 ml-4'>{t('common.qr-code')}</div>
            <button
              className='btn btn-ghost btn-md'
              type='button'
              onClick={() => dialogRef.current?.close()}>
              <Add
                size={24}
                className='rotate-45'
              />
            </button>
          </div>
          <LoadingLayout promise={isLoading}>
            {() =>
              qrcode ? (
                <QrCodeView
                  data={qrcode}
                  label={userInfo?.NickName}
                  avatarUrl={userInfo?.HeadImg}
                  subtitle={userInfo?.VChatID}
                  onForward={(url) => {
                    forwardFormRef.current?.open();
                    setCode(url);
                  }}
                />
              ) : null
            }
          </LoadingLayout>
        </Modal>
        <Modal
          clickOutsideToClose
          contentClassName='w-[400px] h-75dvh'
          onClose={() => {
            setCode('');
          }}
          bottomCloseButton
          ref={forwardFormRef}>
          <ForwardForm
            className='h-full'
            title={t('personal-info.share-to')}
            onForward={handleForward}
          />
        </Modal>
      </>
    );
  },
);
export default QrCodeModal;
